import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject } from '@angular/core';
import { Element, ElementData, Session } from '@app/core/models/courses.model';
import { Resp } from '@app/core/services/api.service';
import { CourseService } from '@app/core/services/course.service';
import { safeEmpty } from '@app/shared/utils';
import { SIDENAV_DATA } from '@app/shared/modules/template/components/sidenav';
import { flattenDeep, nth } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { CourseResolverData } from '../../resolvers/course.resolver';
import { mapTypeToIcon } from '../../utils/type-mapper';

export interface SidenavLearningUnitResponse {
  element: any;
  next: Element;
  previous: Element;
}

export interface ConcludedElement {
  concluido: number;
  porcentagem: number;
}

@Component({
  templateUrl: './sidenav-learning-unit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavLearningUnitComponent {
  private readonly _cdrf = inject(ChangeDetectorRef);
  private readonly _courseService = inject(CourseService);
  private readonly componentData = inject(SIDENAV_DATA);
  public data: CourseResolverData;
  public selectedSession$ = new BehaviorSubject<Session>(undefined);
  public selectedElement$ = new BehaviorSubject<Element>(undefined);
  public elementData$ = new BehaviorSubject(undefined);
  public selectedElement: Element;
  public selectedSessionSlug: string;
  public typeIcon = mapTypeToIcon;

  constructor() {
    this.data = this.componentData.snapshot.courseResolverData;
    this.selectedSession$.next(this.componentData.snapshot.courseResolverData.selectedSession);
    if (
      this.data.courseResponse.sessoes &&
      this.data.courseResponse.sessoes.length &&
      this.data.courseResponse.sessoes[0].elementos &&
      this.data.courseResponse.sessoes[0].elementos.length
    ) {
      this.selectedSessionSlug = this.data.courseResponse.sessoes[0].slug;
      this.selectedElement = this.componentData.snapshot.courseResolverData.selectedElement;
      this.selectedElement$.next(this.selectedElement);
    }

    this.data = this.componentData.snapshot.courseResolverData;
    this.selectedSession$.next(this.componentData.snapshot.courseResolverData.selectedSession);
    this.selectedElement$
      .pipe(
        startWith(this.componentData.snapshot.courseResolverData.selectedElement),
        distinctUntilChanged(),
        switchMap((element) => this.getElementData(element).pipe(safeEmpty())),
        filter((elementData) => !!elementData.tipo),
        tap((res) => this.markAsRead(res)),
        map((elementData) => ({
          elementData,
          next: this.findNextElement(elementData),
          previous: this.findPreviousElement(elementData),
        })),
      )
      .subscribe((value) => this.elementData$.next(value));
  }

  public onChangeElement(session: any, element: Element) {
    this.selectedSessionSlug = session.slug;
    this.selectedSession$.next(session);
    this.selectedElement = element;
    this.selectedElement$.next(element);
  }

  public onChangeElementButton(element: Element) {
    this.selectedSessionSlug = element.session.slug;
    this.selectedSession$.next(element.session);
    this.selectedElement = element;
    this.selectedElement$.next(element);
  }

  private getElementData(element: Element): Observable<Resp<ElementData>> {
    const slug = this.data.courseResponse.slug;
    if (this.data.courseResponse.vitrine) {
      return this._courseService.getElementByVitrine(slug, element.slug);
    }
    return this._courseService.getElement(this.data.courseResponse.evento.id, slug, element.slug);
  }

  private findNextElement(elementData: ElementData): Element {
    const sessoes = this.data.courseResponse.sessoes;
    const data = flattenDeep(sessoes.map((session) => session.elementos.map((element) => ({ ...element, session }))));
    const index = data.findIndex(
      ({ slug, session }) => slug === elementData.slug && session.slug === elementData.sessao.slug,
    );
    return nth(data, index + 1);
  }

  private findPreviousElement(elementData: ElementData): Element {
    const sessoes = this.data.courseResponse.sessoes;
    const data = flattenDeep(sessoes.map((session) => session.elementos.map((element) => ({ ...element, session }))));
    const index = data.findIndex(
      ({ slug, session }) => slug === elementData.slug && session.slug === elementData.sessao.slug,
    );
    if (!!index) {
      return nth(data, index - 1);
    }
    return null;
  }

  private markAsRead(element: ElementData) {
    if (element.tipo === 'avaliacao' || this.data.courseResponse.vitrine || !!element?.sessao?.elemento?.approved) {
      return;
    }

    of(true)
      .pipe(
        delay(3000),
        switchMap(() =>
          this._courseService.markAsRead(
            this.data.courseResponse.evento.id,
            this.data.courseResponse.slug,
            element.slug,
          ),
        ),
        catchError(() => of({ ret: 0, data: null })),
        map((res) => (res.ret === 1 ? res.data : null)),
        tap((res) => {
          this.data.courseResponse.porcentagem = res.porcentagem;
          this.data.courseResponse.sessoes = this.data.courseResponse.sessoes.map((session) => {
            session.elementos = session.elementos.map((e) => {
              if (e.slug === element.slug) {
                e.finalizado = 1;
              }
              return e;
            });
            return session;
          });
          this._cdrf.markForCheck();
        }),
      )
      .subscribe();
  }
}
